import { DateRangePicker as OriginalDateRangePicker } from "@wojtekmaj/react-daterange-picker";
import { useContext, useState, useEffect, useRef } from "react";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-regular-svg-icons";
import { FilterContext } from "./filters/DashboardFilterContextProvider.jsx";
import {useTranslation} from "react-i18next";

export default function DateRangePicker() {
    let {t} = useTranslation();

    const {
        since, setSince,
        until, setUntil,
    } = useContext(FilterContext);

    const [calendarOpen, setCalendarOpen] = useState(false);
    const calendarRef = useRef(null);

    const setQuickRange = (type) => {
        const today = moment();
        let startDate, endDate;

        switch (type) {
            case "today":
                startDate = today.clone().startOf("day");
                endDate = today.clone().endOf("day");
                break;
            case "yesterday":
                startDate = today.clone().subtract(1, "days").startOf("day");
                endDate = today.clone().subtract(1, "days").endOf("day");
                break;
            case "last7Days":
                startDate = today.clone().subtract(6, "days").startOf("day");
                endDate = today.clone().endOf("day");
                break;
            case "last14Days":
                startDate = today.clone().subtract(13, "days").startOf("day");
                endDate = today.clone().endOf("day");
                break;
            case "last30Days":
                startDate = today.clone().subtract(29, "days").startOf("day");
                endDate = today.clone().endOf("day");
                break;
            case "thisMonth":
                startDate = today.clone().startOf("month");
                endDate = today.clone().endOf("month");
                break;
            case "lastMonth":
                startDate = today.clone().subtract(1, "months").startOf("month");
                endDate = today.clone().subtract(1, "months").endOf("month");
                break;
            case "last90Days":
                startDate = today.clone().subtract(89, "days").startOf("day");
                endDate = today.clone().endOf("day");
                break;
            default:
                return;
        }

        setSince(startDate);
        setUntil(endDate);
        setCalendarOpen(false);
    };

    // Add custom buttons to `react-calendar__navigation`
    useEffect(() => {
        if (calendarRef.current) {
            const navigation = calendarRef.current.querySelector(
                ".react-calendar__navigation"
            );
            if (navigation && !navigation.querySelector(".custom-buttons")) {
                const customButtons = document.createElement("div");
                customButtons.className = "custom-buttons flex flex-wrap gap-x-1 gap-y-0.5 p-1";

                const buttons = [
                    { label: t('date.label.today'), onClick: () => setQuickRange("today") },
                    { label: t('date.label.yesterday'), onClick: () => setQuickRange("yesterday") },
                    { label: t('date.label.thisMonth'), onClick: () => setQuickRange("thisMonth") },
                    { label: t('date.label.lastMonth'), onClick: () => setQuickRange("lastMonth") },
                    { label: t('date.label.last7Days'), onClick: () => setQuickRange("last7Days") },
                    { label: t('date.label.last14Days'), onClick: () => setQuickRange("last14Days") },
                    { label: t('date.label.last30Days'), onClick: () => setQuickRange("last30Days") },
                    { label: t('date.label.last90Days'), onClick: () => setQuickRange("last90Days") },
                ];

                buttons.forEach((btn) => {
                    const button = document.createElement("button");
                    button.className =
                        "btn bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600 px-2 py-1 rounded " +
                        "border-gray-300 hover:border-gray-500 text-xs";
                    button.innerText = btn.label;
                    button.onclick = btn.onClick;
                    customButtons.appendChild(button);
                });

                navigation.append(customButtons);
            }
        }
    }, [calendarOpen]);

    return (
        <OriginalDateRangePicker
            className={
                "btn cursor-pointer bg-white text-gray-600 border-gray-400 hover:text-black hover:border-black dark:text-white dark:bg-transparent dark:border-gray-500 dark:hover:border-gray-300"
            }
            value={[since.toDate(), until.toDate()]}
            onChange={(dateRange) => {
                setSince(moment(dateRange[0]));
                setUntil(moment(dateRange[1]));
            }}
            onCalendarOpen={() => setCalendarOpen(true)}
            onCalendarClose={() => setCalendarOpen(false)}
            clearIcon={null}
            calendarIcon={<FontAwesomeIcon icon={faCalendarDays} />}
            calendarProps={{
                inputRef: calendarRef,
            }}
        />
    );
}
